import React, { ChangeEvent, ClipboardEvent, forwardRef, ForwardedRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../../index';
import { usePasteEvent } from '../../../hooks';
import { getFilteredClasses } from '../../../utils';
import { IInput } from './types';
import styles from './styles.module.scss';

const Input = forwardRef(({ className, error, onChange, onPaste, onPasteChange, sanitize, type = 'text', value = '', ...rest }: IInput, ref: ForwardedRef<HTMLInputElement>) => {
  const [isTextType, setIsTextType] = useState(!type || type === 'text');
  const [controlledValue, setControlledValue] = useState(value);
  const errorClass = error ? 'border-danger' : '';
  const passwordClass = type === 'password' ? styles.password : '';
  const filteredClasses = getFilteredClasses([className, styles.input, passwordClass, errorClass]);
  const inputRef = useRef<HTMLInputElement>(null);
  const handlePasteEvent = usePasteEvent(inputRef);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    const sanitizedInput = DOMPurify.sanitize(text);
    const sanitizedEvent = { ...event, target: { ...event.target, value: sanitizedInput } };
    const { xevent, xinput } = sanitize ? { xevent: sanitizedEvent, xinput: sanitizedInput } : { xevent: event, xinput: text };
    setControlledValue(xinput);

    if (onChange) {
      onChange(xevent);
    }

    return xevent;
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const newVal = handlePasteEvent({ event });
    const updatedValue = sanitize ? DOMPurify.sanitize(newVal) : newVal;
    setControlledValue(updatedValue);

    // expose the complete value of the textarea when pasting (similar to onChange)
    if (onPasteChange) {
      onPasteChange(updatedValue);
    }

    if (onPaste) {
      onPaste(event);
    }

    return event;
  };

  useEffect(() => {
    setControlledValue(value);
  }, [value]);

  return (
    <>
      <input
        autoComplete="off"
        className={filteredClasses}
        data-testid="input"
        onChange={handleChange}
        onPaste={handlePaste}
        ref={inputRef}
        type={isTextType ? 'text' : 'password'}
        value={controlledValue}
        {...rest}
      />
      {type === 'password' && (
        <Button
          aria-label="Show password as plain text. Warning: this will display your password on the screen."
          className="position-absolute r-0"
          icon={isTextType ? 'eye' : 'eye-slash'}
          iconNoMargin
          onClick={() => setIsTextType(!isTextType)}
          variant="transparent"
        />
      )}
    </>
  );
});

export { Input };
